import { useState, useCallback, useMemo } from 'react';
import {
  validateQuestion,
  validateAllQuestions as validateAllQuestionsUtil,
  findDuplicates as findDuplicatesUtil,
  getQualityScore as getQualityScoreUtil
} from '../../utils/llm-generator/questionValidators';

/**
 * Custom Hook für Fragen-Validierung
 * 
 * Verwaltet:
 * - Validierungsfehler
 * - Quality Scores
 * - Duplikat-Erkennung
 */
export function useQuestionValidation(questions) {
  const [validationErrors, setValidationErrors] = useState({});
  const [lastValidationTime, setLastValidationTime] = useState(null);
  
  /**
   * Validate all questions and update error state
   * @returns {Object} Errors object keyed by question index
   */
  const validateAllQuestions = useCallback(() => {
    const errors = {};
    questions.forEach((q, i) => {
      const questionErrors = validateQuestion(q);
      if (questionErrors.length > 0) {
        errors[i] = questionErrors;
      }
    });
    setValidationErrors(errors);
    setLastValidationTime(Date.now());
    return errors;
  }, [questions]);
  
  /**
   * Validate a single question
   * @param {number} index - Question index
   * @returns {Array} Array of error strings
   */
  const validateSingleQuestion = useCallback((index) => {
    if (index < 0 || index >= questions.length) return [];
    
    const errors = validateQuestion(questions[index]);
    
    setValidationErrors(prev => {
      if (errors.length > 0) {
        return { ...prev, [index]: errors };
      } else {
        const updated = { ...prev };
        delete updated[index];
        return updated;
      }
    });
    
    return errors;
  }, [questions]);
  
  /**
   * Get quality score for a question
   */
  const getQualityScore = useCallback((question) => {
    return getQualityScoreUtil(question);
  }, []);
  
  /**
   * Find duplicate questions
   * @returns {Array} Array of duplicate pairs
   */
  const findDuplicates = useCallback(() => {
    return findDuplicatesUtil(questions);
  }, [questions]);
  
  /**
   * Check if a specific question has errors
   */
  const hasErrors = useCallback((index) => {
    return validationErrors[index]?.length > 0;
  }, [validationErrors]);
  
  /**
   * Get errors for a specific question
   */
  const getErrors = useCallback((index) => {
    return validationErrors[index] || [];
  }, [validationErrors]);
  
  /**
   * Clear all validation errors
   */
  const clearErrors = useCallback(() => {
    setValidationErrors({});
  }, []);
  
  /**
   * Clear errors for a specific question
   */
  const clearQuestionErrors = useCallback((index) => {
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  }, []);
  
  // Computed values
  const totalErrors = useMemo(() => {
    return Object.values(validationErrors).reduce((sum, errors) => sum + errors.length, 0);
  }, [validationErrors]);
  
  const questionsWithErrors = useMemo(() => {
    return Object.keys(validationErrors).length;
  }, [validationErrors]);
  
  const isValid = useMemo(() => {
    return totalErrors === 0;
  }, [totalErrors]);
  
  const averageQualityScore = useMemo(() => {
    if (questions.length === 0) return 0;
    const total = questions.reduce((sum, q) => sum + getQualityScoreUtil(q), 0);
    return Math.round(total / questions.length);
  }, [questions]);
  
  return {
    // State
    validationErrors,
    lastValidationTime,
    
    // Validation Actions
    validateAllQuestions,
    validateSingleQuestion,
    
    // Quality & Duplicates
    getQualityScore,
    findDuplicates,
    
    // Error Helpers
    hasErrors,
    getErrors,
    clearErrors,
    clearQuestionErrors,
    
    // Computed
    totalErrors,
    questionsWithErrors,
    isValid,
    averageQualityScore
  };
}

export default useQuestionValidation;
